import apiClient from '../client';
import { APIResponse } from '@memriti/shared';

export interface ResumeInfo {
  id: number;
  filename: string;
  name?: string;  // Candidate's name
  skills: string[];
  experience: any[];
  education: any[];
  projects: any[];
  is_processed: boolean;
  processing_error?: string;
}

export interface Question {
  id: string;
  text: string;
  category: string;
}

export interface QuestionCategory {
  categories: string[];
  counts: Record<string, number>;
}

export interface AIResponse {
  question_id: string;
  question_text: string;
  question_category: string;
  ai_response: string;
  status: 'pending' | 'generating' | 'completed' | 'failed';
}

export const resumeService = {
  async uploadResume(file: File): Promise<APIResponse<ResumeInfo>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post('/api/v1/resume/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  async getLatestResume(): Promise<APIResponse<ResumeInfo>> {
    const response = await apiClient.get('/api/v1/resume/latest');
    return response.data;
  },

  async getUserResumes(): Promise<APIResponse<ResumeInfo[]>> {
    const response = await apiClient.get('/api/v1/resume/');
    return response.data;
  },

  async getQuestions(resumeId: number, category?: string): Promise<APIResponse<Question[]>> {
    const params = category ? { category } : {};
    const response = await apiClient.get(`/api/v1/resume/${resumeId}/questions`, { params });
    return response.data;
  },

  async getQuestionCategories(resumeId: number): Promise<APIResponse<QuestionCategory>> {
    const response = await apiClient.get(`/api/v1/resume/${resumeId}/questions/categories`);
    return response.data;
  },

  async generateAnswers(resumeId: number): Promise<APIResponse<AIResponse[]>> {
    const response = await apiClient.post(`/api/v1/resume/${resumeId}/generate_answers`);
    return response.data;
  },

  async getResumeResponses(resumeId: number): Promise<APIResponse<AIResponse[]>> {
    const response = await apiClient.get(`/api/v1/resume/${resumeId}/responses`);
    return response.data;
  },

  async deleteResume(resumeId: number): Promise<APIResponse<void>> {
    const response = await apiClient.delete(`/api/v1/resume/${resumeId}`);
    return response.data;
  },

  async askCustomQuestion(resumeId: number, question: string): Promise<APIResponse<AIResponse>> {
    const response = await apiClient.post(`/api/v1/resume/${resumeId}/custom_question`, {
      question
    });
    return response.data;
  },
};